import React, { useState, useEffect, useRef } from 'react';
import ReactQuill from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import { artikelService } from '../../../services/artikelService';
import { useAuth } from '../../Admin/context/AuthContext';

import {
    FaPlus, FaEdit, FaTrash, FaSearch, FaEye, FaEyeSlash,
    FaTimes, FaSave, FaCheckCircle, FaExclamationTriangle,
    FaChevronLeft, FaChevronRight, FaPaperPlane, FaClipboardCheck,
    FaThumbsUp, FaThumbsDown, FaGlobe, FaArchive, FaFileAlt,
    FaStar, FaMapPin, FaUsers, FaUpload, FaImage, FaFile, FaLink, FaCheckSquare
} from 'react-icons/fa';

const statusConfig = {
    draft: { label: 'Draft', color: 'bg-gray-100 text-gray-600', icon: FaFileAlt },
    submitted: { label: 'Submitted', color: 'bg-blue-100 text-blue-600', icon: FaPaperPlane },
    reviewed: { label: 'Reviewed', color: 'bg-yellow-100 text-yellow-600', icon: FaClipboardCheck },
    approved: { label: 'Approved', color: 'bg-purple-100 text-purple-600', icon: FaThumbsUp },
    published: { label: 'Published', color: 'bg-green-100 text-green-600', icon: FaGlobe },
    rejected: { label: 'Rejected', color: 'bg-red-100 text-red-600', icon: FaThumbsDown },
    archived: { label: 'Archived', color: 'bg-gray-100 text-gray-400', icon: FaArchive },
};

const ManajemenArtikel = () => {
    const [artikels, setArtikels] = useState([]);
    const [statistics, setStatistics] = useState({});
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [filterStatus, setFilterStatus] = useState('');
    const [filterKategori, setFilterKategori] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [showForm, setShowForm] = useState(false);
    const [editingArtikel, setEditingArtikel] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [notification, setNotification] = useState(null);
    const [deleteConfirm, setDeleteConfirm] = useState(null);
    const [reviewModal, setReviewModal] = useState(null);
    const [reviewAction, setReviewAction] = useState('approve');
    const [reviewCatatan, setReviewCatatan] = useState('');

    const { user } = useAuth(); 

    const hasPermission = (permission) => {
        // Admin selalu boleh (opsional)
        if (user?.roles?.includes('admin')) return true;

        // Cek di daftar permissions
        if (!user?.permissions) return false;
        return user.permissions.includes(permission);
    };

    const hasRole = (role) => {
        return user?.roles?.includes(role);
    };

    // File states
    const [gambarFile, setGambarFile] = useState(null);
    const [thumbnailFile, setThumbnailFile] = useState(null);
    const [lampiranFile, setLampiranFile] = useState(null);
    const [gambarPreview, setGambarPreview] = useState(null);
    const [thumbnailPreview, setThumbnailPreview] = useState(null);

    const [formData, setFormData] = useState({
        judul: '',
        kategori: 'berita',
        ringkasan: '',
        konten: '',
        tags: '',
        is_featured: false,
        is_pinned: false,
    });

    const [errors, setErrors] = useState({});

    // WYSIWYG Editor config
    const quillRef = useRef(null);

    const modules = {
        toolbar: {
            container: [
                [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
                ['bold', 'italic', 'underline', 'strike'],
                [{ 'color': [] }, { 'background': [] }],
                [{ 'list': 'ordered' }, { 'list': 'bullet' }],
                [{ 'indent': '-1' }, { 'indent': '+1' }],
                [{ 'align': [] }],
                ['blockquote', 'code-block'],
                ['link', 'image', 'video'],
                ['clean']
            ],
            handlers: {
                image: imageHandler
            }
        }
    };

    const formats = [
        'header', 'bold', 'italic', 'underline', 'strike',
        'color', 'background', 'list', 'bullet', 'indent',
        'align', 'blockquote', 'code-block', 'link', 'image', 'video'
    ];

    // Custom image handler untuk upload ke server
    function imageHandler() {
        const input = document.createElement('input');
        input.setAttribute('type', 'file');
        input.setAttribute('accept', 'image/*');
        input.click();

        input.onchange = async () => {
            const file = input.files[0];
            if (!file) return;

            const formData = new FormData();
            formData.append('image', file);

            try {
                const result = await artikelService.uploadEditorImage(formData);
                if (result.success) {
                    const quill = quillRef.current.getEditor();
                    const range = quill.getSelection();
                    quill.insertEmbed(range.index, 'image', result.data.url);
                } else {
                    showNotification('Gagal upload gambar', 'error');
                }
            } catch (error) {
                showNotification('Gagal upload gambar', 'error');
            }
        };
    }

    const loadArtikels = async (page = 1) => {
        setLoading(true);
        const params = { page };
        if (searchTerm) params.search = searchTerm;
        if (filterStatus) params.status = filterStatus;
        if (filterKategori) params.kategori = filterKategori;

        const result = await artikelService.getArtikels(params);
        if (result.success) {
            setArtikels(result.data.data || []);
            setTotalPages(result.data.last_page || 1);
            setCurrentPage(result.data.current_page || 1);
        } else {
            showNotification(result.message, 'error');
        }
        setLoading(false);
    };

    const loadStatistics = async () => {
        const result = await artikelService.getStatistics();
        if (result.success) {
            setStatistics(result.data);
        }
    };

    useEffect(() => {
        loadArtikels(1);
        loadStatistics();
    }, []);

    const showNotification = (message, type = 'success') => {
        setNotification({ message, type });
        setTimeout(() => setNotification(null), 3000);
    };

    const handleCreate = () => {
        setEditingArtikel(null);
        setFormData({
            judul: '', kategori: 'berita', ringkasan: '', konten: '',
            tags: '', is_featured: false, is_pinned: false,
        });
        setGambarFile(null);
        setThumbnailFile(null);
        setLampiranFile(null);
        setGambarPreview(null);
        setThumbnailPreview(null);
        setErrors({});
        setShowForm(true);
    };

    const handleEdit = (artikel) => {
        setEditingArtikel(artikel);
        setFormData({
            judul: artikel.judul || '',
            kategori: artikel.kategori || 'berita',
            ringkasan: artikel.ringkasan || '',
            konten: artikel.konten || '',
            tags: artikel.tags ? artikel.tags.join(', ') : '',
            is_featured: artikel.is_featured || false,
            is_pinned: artikel.is_pinned || false,
        });
        setGambarFile(null);
        setThumbnailFile(null);
        setLampiranFile(null);
        setGambarPreview(artikel.gambar ? getFileUrl(artikel.gambar) : null);
        setThumbnailPreview(artikel.thumbnail ? getFileUrl(artikel.thumbnail) : null);
        setErrors({});
        setShowForm(true);
    };

    const getFileUrl = (path) => {
        if (!path) return '';
        if (path.startsWith('http')) return path;
        return `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/${path}`;
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleKontenChange = (value) => {
        setFormData(prev => ({ ...prev, konten: value }));
        if (errors.konten) {
            setErrors(prev => ({ ...prev, konten: '' }));
        }
    };

    // Handle file uploads
    const handleGambarChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setGambarFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setGambarPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleThumbnailChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setThumbnailFile(file);
            const reader = new FileReader();
            reader.onloadend = () => setThumbnailPreview(reader.result);
            reader.readAsDataURL(file);
        }
    };

    const handleLampiranChange = (e) => {
        setLampiranFile(e.target.files[0]);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        setSubmitting(true);

        const submitData = new FormData();
        submitData.append('judul', formData.judul);
        submitData.append('kategori', formData.kategori);
        submitData.append('ringkasan', formData.ringkasan || '');
        submitData.append('konten', formData.konten);
        submitData.append('is_featured', formData.is_featured ? 1 : 0);
        submitData.append('is_pinned', formData.is_pinned ? 1 : 0);

        // Tags
        const tags = formData.tags ? formData.tags.split(',').map(t => t.trim()).filter(t => t) : [];
        tags.forEach((tag, i) => submitData.append(`tags[${i}]`, tag));

        // Files
        if (gambarFile) submitData.append('gambar', gambarFile);
        if (thumbnailFile) submitData.append('thumbnail', thumbnailFile);
        if (lampiranFile) submitData.append('file_lampiran', lampiranFile);

        let result;
        if (editingArtikel) {
            result = await artikelService.updateArtikel(editingArtikel.id, submitData);
        } else {
            result = await artikelService.createArtikel(submitData);
        }

        if (result.success) {
            showNotification(result.message || 'Artikel berhasil disimpan', 'success');
            setShowForm(false);
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message || 'Gagal menyimpan artikel', 'error');
        }
        setSubmitting(false);
    };

    const handleDelete = (artikel) => setDeleteConfirm(artikel);

    const confirmDelete = async () => {
        if (!deleteConfirm) return;
        const result = await artikelService.deleteArtikel(deleteConfirm.id);
        if (result.success) {
            showNotification('Artikel berhasil dihapus', 'success');
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message, 'error');
        }
        setDeleteConfirm(null);
    };

    const handleSubmitForReview = async (id) => {
        const result = await artikelService.submitArtikel(id);
        if (result.success) {
            showNotification(result.message, 'success');
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message, 'error');
        }
    };

    const openReviewModal = (artikel, type) => {
        setReviewModal(artikel);
        setReviewAction('approve');
        setReviewCatatan('');
    };

    const handleReviewSubmit = async () => {
        if (!reviewModal) return;
        const result = await artikelService.reviewArtikel(reviewModal.id, reviewAction, reviewCatatan);
        if (result.success) {
            showNotification(result.message, 'success');
            setReviewModal(null);
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message, 'error');
        }
    };

    const handleApproveSubmit = async () => {
        if (!reviewModal) return;
        const result = await artikelService.approveArtikel(reviewModal.id, reviewAction, reviewCatatan);
        if (result.success) {
            showNotification(result.message, 'success');
            setReviewModal(null);
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message, 'error');
        }
    };

    const handlePublish = async (id) => {
        const result = await artikelService.publishArtikel(id);
        if (result.success) {
            showNotification(result.message, 'success');
            loadArtikels(currentPage);
            loadStatistics();
        }
    };

    const handleUnpublish = async (id) => {
        const result = await artikelService.unpublishArtikel(id);
        if (result.success) {
            showNotification(result.message, 'success');
            loadArtikels(currentPage);
            loadStatistics();
        }
    };

    const handleUnarchive = async (id) => {
        if (!window.confirm('Kembalikan artikel ini ke Draft agar bisa diedit lagi?')) return;
        const result = await artikelService.unarchiveArtikel(id);
        if (result.success) {
            showNotification(result.message || 'Artikel dikembalikan ke Draft', 'success');
            loadArtikels(currentPage);
            loadStatistics();
        } else {
            showNotification(result.message || 'Gagal mengembalikan artikel', 'error');
        }
    };

    const renderActions = (artikel) => {
        const actions = [];

        // Submit for review — butuh permission "artikel.submit"
        if ((artikel.status === 'draft' || artikel.status === 'rejected') && hasPermission('artikel.submit')) {
            actions.push(
                <button key="submit" onClick={() => handleSubmitForReview(artikel.id)}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Submit untuk Review">
                    <FaPaperPlane />
                </button>
            );
        }

        // Review — butuh "artikel.review"
        if (artikel.status === 'submitted' && hasPermission('artikel.review')) {
            actions.push(
                <button key="review" onClick={() => openReviewModal(artikel, 'review')}
                    className="p-2 text-yellow-500 hover:bg-yellow-50 rounded-lg" title="Review">
                    <FaClipboardCheck />
                </button>
            );
        }

        // Approve — butuh "artikel.approve"
        if (artikel.status === 'reviewed' && hasPermission('artikel.approve')) {
            actions.push(
                <button key="approve" onClick={() => openReviewModal(artikel, 'approve')}
                    className="p-2 text-purple-500 hover:bg-purple-50 rounded-lg" title="Approve">
                    <FaThumbsUp />
                </button>
            );
        }

        // Publish — butuh "artikel.publish"
        if (artikel.status === 'approved' && hasPermission('artikel.publish')) {
            actions.push(
                <button key="publish" onClick={() => handlePublish(artikel.id)}
                    className="p-2 text-green-500 hover:bg-green-50 rounded-lg" title="Publish">
                    <FaGlobe />
                </button>
            );
        }

        // Unpublish — butuh "artikel.publish"
        if (artikel.status === 'published' && hasPermission('artikel.publish')) {
            actions.push(
                <button key="unpublish" onClick={() => handleUnpublish(artikel.id)}
                    className="p-2 text-gray-500 hover:bg-gray-50 rounded-lg" title="Unpublish">
                    <FaArchive />
                </button>
            );
        }

        // ✅ Edit — butuh "artikel.update"
        if (hasPermission('artikel.update')) {
            actions.push(
                <button key="edit" onClick={() => handleEdit(artikel)}
                    className="p-2 text-blue-500 hover:bg-blue-50 rounded-lg" title="Edit">
                    <FaEdit />
                </button>
            );
        }

        // ✅ Delete — butuh "artikel.delete"  ← INI YANG HILANG
        if (hasPermission('artikel.delete')) {
            actions.push(
                <button key="delete" onClick={() => handleDelete(artikel)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Hapus">
                    <FaTrash />
                </button>
            );
        }

        if (artikel.status === 'archived' && hasPermission('artikel.rejected')) {
            actions.push(
                <button key="delete" onClick={() => handleUnarchive(artikel.id)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg" title="Hapus">
                    <FaCheckSquare />
                </button>
            );
        }

        return actions.length > 0 ? actions : (
            <span className="text-xs text-gray-400 italic">Tidak ada aksi</span>
        );
    };

    const StatusBadge = ({ status }) => {
        const config = statusConfig[status] || statusConfig.draft;
        const Icon = config.icon;
        return (
            <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-medium ${config.color}`}>
                <Icon className="mr-1" />
                {config.label}
            </span>
        );
    };

    return (
        <div>
            {/* Header */}
            <div className="flex flex-col md:flex-row md:items-center justify-between mb-6 gap-4">
                <div>
                    <h2 className="text-2xl font-bold text-[#0F4C3A]">Manajemen Artikel & Berita</h2>
                </div>
                {hasPermission('artikel.create') && (
                    <button onClick={handleCreate} className="btn-primary flex items-center justify-center">
                        <FaPlus className="mr-2" /> Tambah Artikel
                    </button>
                )}
            </div>

            {/* Statistics */}
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 mb-6">
                {Object.entries(statistics).map(([key, value]) => (
                    <div key={key} className="bg-white rounded-lg p-3 shadow-sm text-center">
                        <p className="text-2xl font-bold text-[#0F4C3A]">{value}</p>
                        <p className="text-xs text-gray-500 capitalize">{key}</p>
                    </div>
                ))}
            </div>

            {notification && (
                <div className={`mb-6 p-4 rounded-lg flex items-center ${notification.type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'
                    }`}>
                    {notification.type === 'success' ? <FaCheckCircle className="mr-3" /> : <FaExclamationTriangle className="mr-3" />}
                    {notification.message}
                </div>
            )}

            {/* Filters */}
            <div className="bg-white rounded-xl p-4 shadow-sm mb-6 flex flex-col md:flex-row gap-3">
                <div className="relative flex-1">
                    <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                    <input type="text" value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                        placeholder="Cari artikel..." className="w-full pl-10 pr-4 py-2.5 border rounded-lg" />
                </div>
                <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} className="px-4 py-2.5 border rounded-lg">
                    <option value="">Semua Status</option>
                    {Object.keys(statusConfig).map(key => (
                        <option key={key} value={key}>{statusConfig[key].label}</option>
                    ))}
                </select>
                <select value={filterKategori} onChange={(e) => setFilterKategori(e.target.value)} className="px-4 py-2.5 border rounded-lg">
                    <option value="">Semua Kategori</option>
                    <option value="berita">Berita</option>
                    <option value="kegiatan">Kegiatan</option>
                    <option value="artikel">Artikel</option>
                    <option value="pengumuman">Pengumuman</option>
                </select>
                <button onClick={() => loadArtikels(1)} className="btn-primary">Filter</button>
            </div>

            {/* Table */}
            <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="overflow-x-auto">
                    <table className="w-full">
                        <thead className="bg-gray-50">
                            <tr className="text-left text-sm text-gray-500">
                                <th className="p-4">Judul</th>
                                <th className="p-4">Kategori</th>
                                <th className="p-4">Status</th>
                                <th className="p-4">Author</th>
                                <th className="p-4">Tanggal</th>
                                <th className="p-4 text-center">Aksi</th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr><td colSpan={6} className="p-8 text-center">
                                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2E7D32] mx-auto"></div>
                                </td></tr>
                            ) : artikels.length === 0 ? (
                                <tr><td colSpan={6} className="p-8 text-center text-gray-500">Tidak ada data</td></tr>
                            ) : (
                                artikels.map((artikel) => (
                                    <tr key={artikel.id} className="border-b hover:bg-gray-50">
                                        <td className="p-4">
                                            <div className="flex items-center">
                                                {artikel.thumbnail && (
                                                    <img src={getFileUrl(artikel.thumbnail)} alt="" className="w-12 h-12 rounded object-cover mr-3" />
                                                )}
                                                <div>
                                                    <p className="font-medium text-gray-700 line-clamp-1">{artikel.judul}</p>
                                                    <div className="flex items-center gap-2 mt-1">
                                                        {artikel.is_featured && <FaStar className="text-yellow-500 text-xs" />}
                                                        {artikel.is_pinned && <FaMapPin className="text-blue-500 text-xs" />}
                                                        {artikel.file_lampiran && <FaFile className="text-purple-500 text-xs" />}
                                                        <span className="text-xs text-gray-400">Views: {artikel.views}</span>
                                                    </div>
                                                </div>
                                            </div>
                                        </td>
                                        <td className="p-4 capitalize text-sm text-gray-600">{artikel.kategori}</td>
                                        <td className="p-4"><StatusBadge status={artikel.status} /></td>
                                        <td className="p-4 text-sm text-gray-600">{artikel.author}</td>
                                        <td className="p-4 text-sm text-gray-500">{new Date(artikel.created_at).toLocaleDateString('id-ID')}</td>
                                        <td className="p-4">
                                            <div className="flex justify-center space-x-1">{renderActions(artikel)}</div>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {totalPages > 1 && (
                    <div className="flex items-center justify-between px-4 py-3 border-t">
                        <p className="text-sm text-gray-500">Halaman {currentPage} dari {totalPages}</p>
                        <div className="flex space-x-2">
                            <button onClick={() => loadArtikels(currentPage - 1)} disabled={currentPage === 1}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                <FaChevronLeft />
                            </button>
                            <button onClick={() => loadArtikels(currentPage + 1)} disabled={currentPage === totalPages}
                                className="p-2 border rounded-lg hover:bg-gray-50 disabled:opacity-50">
                                <FaChevronRight />
                            </button>
                        </div>
                    </div>
                )}
            </div>

            {/* Form Modal */}
            {showForm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto">
                    <div className="bg-white rounded-2xl max-w-4xl w-full my-8 max-h-[calc(100vh-4rem)] flex flex-col shadow-2xl">
                        {/* Header Sticky */}
                        <div className="flex justify-between items-center p-6 border-b sticky top-0 bg-white rounded-t-2xl z-10">
                            <h3 className="text-xl font-bold text-[#0F4C3A]">
                                {editingArtikel ? 'Edit Artikel' : 'Tambah Artikel'}
                            </h3>
                            <button
                                onClick={() => setShowForm(false)}
                                className="text-gray-400 hover:text-gray-600 p-1 hover:bg-gray-100 rounded-full"
                            >
                                <FaTimes size={20} />
                            </button>
                        </div>

                        {/* Body Scrollable */}
                        <div className="p-6 overflow-y-auto flex-1">
                            <form onSubmit={handleSubmit} className="space-y-4">
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Judul *</label>
                                        <input type="text" name="judul" value={formData.judul} onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg" required />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">Kategori</label>
                                        <select name="kategori" value={formData.kategori} onChange={handleInputChange}
                                            className="w-full px-4 py-2.5 border rounded-lg">
                                            <option value="berita">Berita</option>
                                            <option value="kegiatan">Kegiatan</option>
                                            <option value="artikel">Artikel</option>
                                            <option value="pengumuman">Pengumuman</option>
                                        </select>
                                    </div>
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Ringkasan</label>
                                    <textarea name="ringkasan" value={formData.ringkasan} onChange={handleInputChange}
                                        rows="2" className="w-full px-4 py-2.5 border rounded-lg" />
                                </div>

                                {/* WYSIWYG Editor */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Konten *</label>
                                    <div className="bg-white rounded-lg">
                                        <ReactQuill
                                            ref={quillRef}
                                            theme="snow"
                                            value={formData.konten}
                                            onChange={handleKontenChange}
                                            modules={modules}
                                            formats={formats}
                                            placeholder="Tulis konten artikel di sini..."
                                            style={{ minHeight: '250px' }}
                                        />
                                    </div>
                                </div>

                                {/* Gambar & Thumbnail */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaImage className="inline mr-1" /> Gambar Utama
                                        </label>
                                        <input type="file" accept="image/*" onChange={handleGambarChange}
                                            className="w-full px-4 py-2.5 border rounded-lg" />
                                        <p className="text-xs text-gray-500 mt-1">Format: JPG, PNG, WebP. Maks: 5MB</p>
                                        {gambarPreview && (
                                            <div className="mt-2">
                                                <img src={gambarPreview} alt="Preview" className="max-h-32 object-contain rounded border" />
                                            </div>
                                        )}
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700 mb-1">
                                            <FaImage className="inline mr-1" /> Thumbnail
                                        </label>
                                        <input type="file" accept="image/*" onChange={handleThumbnailChange}
                                            className="w-full px-4 py-2.5 border rounded-lg" />
                                        <p className="text-xs text-gray-500 mt-1">Format: JPG, PNG, WebP. Maks: 2MB</p>
                                        {thumbnailPreview && (
                                            <div className="mt-2">
                                                <img src={thumbnailPreview} alt="Preview" className="max-h-32 object-contain rounded border" />
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* File Lampiran */}
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">
                                        <FaFile className="inline mr-1" /> File Lampiran
                                    </label>
                                    <input type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx" onChange={handleLampiranChange}
                                        className="w-full px-4 py-2.5 border rounded-lg" />
                                    <p className="text-xs text-gray-500 mt-1">
                                        Format: PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX. Maks: 10MB
                                    </p>
                                    {lampiranFile && (
                                        <p className="mt-1 text-sm text-green-600">
                                            ✓ {lampiranFile.name} ({(lampiranFile.size / 1024).toFixed(1)} KB)
                                        </p>
                                    )}
                                    {editingArtikel?.file_name && !lampiranFile && (
                                        <p className="mt-1 text-sm text-gray-500">
                                            File saat ini: {editingArtikel.file_name}
                                        </p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-1">Tags (pisahkan dengan koma)</label>
                                    <input type="text" name="tags" value={formData.tags} onChange={handleInputChange}
                                        className="w-full px-4 py-2.5 border rounded-lg" placeholder="tag1, tag2" />
                                </div>

                                <div className="flex gap-4">
                                    <label className="flex items-center">
                                        <input type="checkbox" name="is_featured" checked={formData.is_featured} onChange={handleInputChange}
                                            className="mr-2" /> Featured (unggulan)
                                    </label>
                                    <label className="flex items-center">
                                        <input type="checkbox" name="is_pinned" checked={formData.is_pinned} onChange={handleInputChange}
                                            className="mr-2" /> Pinned (disematkan paling atas)
                                    </label>
                                </div>

                                {/* Footer Buttons */}
                                <div className="flex justify-end space-x-3 pt-4 border-t sticky bottom-0 bg-white">
                                    <button type="button" onClick={() => setShowForm(false)} className="px-6 py-2.5 border rounded-lg hover:bg-gray-50">
                                        Batal
                                    </button>
                                    <button type="submit" disabled={submitting}
                                        className="flex items-center bg-gradient-to-r from-[#0F4C3A] to-[#2E7D32] text-white px-6 py-2.5 rounded-lg hover:shadow-lg disabled:opacity-50">
                                        {submitting ? 'Menyimpan...' : <><FaSave className="mr-2" /> Simpan</>}
                                    </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}


            {/* Review/Approve Modal */}
            {reviewModal && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6">
                        <h3 className="text-xl font-bold text-[#0F4C3A] mb-4">
                            {reviewModal.status === 'submitted' ? 'Review Artikel' : 'Approve Artikel'}
                        </h3>
                        <p className="text-gray-600 mb-4">{reviewModal.judul}</p>
                        <div className="space-y-3 mb-4">
                            <div className="flex gap-4">
                                <button onClick={() => setReviewAction('approve')}
                                    className={`flex-1 py-2 rounded-lg border-2 ${reviewAction === 'approve' ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                                    <FaThumbsUp className="mx-auto text-green-500" />
                                    <span className="text-sm">Approve</span>
                                </button>
                                <button onClick={() => setReviewAction('reject')}
                                    className={`flex-1 py-2 rounded-lg border-2 ${reviewAction === 'reject' ? 'border-red-500 bg-red-50' : 'border-gray-200'}`}>
                                    <FaThumbsDown className="mx-auto text-red-500" />
                                    <span className="text-sm">Reject</span>
                                </button>
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">Catatan</label>
                                <textarea value={reviewCatatan} onChange={(e) => setReviewCatatan(e.target.value)}
                                    rows="3" className="w-full px-4 py-2 border rounded-lg" />
                            </div>
                        </div>
                        <div className="flex justify-end space-x-3">
                            <button onClick={() => setReviewModal(null)} className="px-6 py-2 border rounded-lg">Batal</button>
                            <button onClick={reviewModal.status === 'submitted' ? handleReviewSubmit : handleApproveSubmit}
                                className="px-6 py-2 bg-[#2E7D32] text-white rounded-lg">
                                Submit
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Modal */}
            {deleteConfirm && (
                <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
                    <div className="bg-white rounded-2xl max-w-md w-full p-6 text-center">
                        <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                            <FaTrash className="text-3xl text-red-500" />
                        </div>
                        <h3 className="text-xl font-bold mb-2">Hapus Artikel?</h3>
                        <p className="text-gray-600 mb-6">Anda akan menghapus "{deleteConfirm.judul}"</p>
                        <div className="flex justify-center gap-3">
                            <button onClick={() => setDeleteConfirm(null)} className="px-6 py-2.5 border rounded-lg">Batal</button>
                            <button onClick={confirmDelete} className="px-6 py-2.5 bg-red-500 text-white rounded-lg">Hapus</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ManajemenArtikel;